import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { useRef, useState } from 'react'
import {
	getAllSystemTechnologies,
	getAllWorkflowTechnologies,
	getObjectSettingsByType,
} from 'src/endpoints'

import { useDiagramContext } from '@/components/Diagram/context/DiagramContext'
import { ModalForm } from '@/components/Modal/ModalForm'
import { SelectFormField, TextFormField } from '@/components/UberForm'
import { useCanGenerateNaming } from '@/components/UberForm/Input/Naming/useCanGenerateNaming'
import { executeNamingOverWorkingData } from '@/endpoints'
import { useApi, useApiRequest } from '@/endpoints/hooks'
import { StructureObjectDto } from '@/endpoints/models'
import { useAppContext, useAppDispatch } from '@/hooks'
import {
	getAction,
	getHeader,
} from '@/pages/User/pages/Home/components/AddModal/utils'
import { useAdjustTableNameUsingPostMutation } from '@/rtkApi'
import { getInitialApiCollectionData } from '@/store/modules/apiCollection/helpers'
import { getInitialApiNodeData } from '@/store/modules/apiNode/helpers'
import { getInitialMappingData } from '@/store/modules/mapping/helpers'
import { getInitialQueueData } from '@/store/modules/queue/helpers'
import { getInitialReadMappingData } from '@/store/modules/readMapping/helpers'
import { getInitialSystemData } from '@/store/modules/system/helpers'
import { getInitialTableData } from '@/store/modules/table/helpers'
import { getInitialViewData } from '@/store/modules/view/helpers'
import { getInitialWorkflowData } from '@/store/modules/workflow/helpers'
import { getInitialWriteMappingData } from '@/store/modules/writeMapping/helpers'
import { NamingDtoTypeEnum, SqlTypeEnum } from '@/types'
import { formatCodeByCase } from '@/utils'
import { uniqueNameValidator } from '@/utils/validators'

import { AddModalProps, FormData } from '../../types'
import { AddModalWriteMapping } from '../AddModalWriteMapping'

export const AddModal = ({
	type,
	onClose,
	onRefresh,
	isDiagramAddModal,
	node,
	isOpen = true,
}: AddModalProps) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const request = useApiRequest()
	const inputRef = useRef(null)
	const systemTechnologies = useApi(getAllSystemTechnologies())
	const workflowTechnologies = useApi(getAllWorkflowTechnologies())
	const objectSettings = useApi(getObjectSettingsByType(type))
	const { diagramRef } = useDiagramContext()

	const isSystem = type === StructureObjectDto.TypeEnum.SYSTEM
	const isWriteMapping = type === StructureObjectDto.TypeEnum.MAPPING_WRITE

	const { generateElementCode, isUpperCase } = useCanGenerateNaming(node)

	const [writeMapping, setWriteMapping] = useState({
		targetTableFullPath: '',
	})

	const [adjustTableNameMutation] = useAdjustTableNameUsingPostMutation()

	const onChangeWriteMapping = (value) => setWriteMapping(value)

	const getDefaultObjectSettings = () => {
		const defaultOs = objectSettings?.data?.find((os) => os.defaultFlag)

		if (defaultOs) {
			return { id: defaultOs.id, code: defaultOs.code, name: defaultOs.name }
		}
	}

	const getNodeData = (name: string, code: string, form: Partial<FormData>) => {
		if (isSystem) {
			return getInitialSystemData(
				name,
				code,
				form.systemTechnologyId,
				form.workflowTechnologyId,
			)
		}

		if (type === StructureObjectDto.TypeEnum.TABLE) {
			const defaultOs = getDefaultObjectSettings()

			return getInitialTableData(name, code, defaultOs)
		}

		if (type === StructureObjectDto.TypeEnum.VIEW) {
			return getInitialViewData(name, code)
		}

		if (type === StructureObjectDto.TypeEnum.MAPPING) {
			const defaultOs = getDefaultObjectSettings()

			return getInitialMappingData(name, code, defaultOs)
		}

		if (type === StructureObjectDto.TypeEnum.MAPPING_READ) {
			return getInitialReadMappingData(name, code)
		}

		if (type === StructureObjectDto.TypeEnum.MAPPING_WRITE) {
			return getInitialWriteMappingData(
				name,
				code,
				form.targetTableId as number,
				writeMapping.targetTableFullPath,
			)
		}

		if (type === StructureObjectDto.TypeEnum.WORKFLOW) {
			return getInitialWorkflowData(name, code)
		}

		if (type === StructureObjectDto.TypeEnum.API_COLLECTION) {
			return getInitialApiCollectionData(name, code)
		}

		if (type === StructureObjectDto.TypeEnum.API_NODE) {
			return getInitialApiNodeData(name, code)
		}

		if (type === StructureObjectDto.TypeEnum.QUEUE) {
			const defaultOs = getDefaultObjectSettings()

			return getInitialQueueData(name, code, defaultOs)
		}

		return { name }
	}

	const getNamingConfig = (name: string) => {
		const serializedData = JSON.stringify({ name })

		switch (type) {
			case StructureObjectDto.TypeEnum.TABLE:
				return [NamingDtoTypeEnum.TABLE_CODE, SqlTypeEnum.TABLE, serializedData] as const
			case StructureObjectDto.TypeEnum.VIEW:
				return [NamingDtoTypeEnum.VIEW_CODE, SqlTypeEnum.VIEW, serializedData] as const
			case StructureObjectDto.TypeEnum.MAPPING:
				return [NamingDtoTypeEnum.MAPPING_CODE, SqlTypeEnum.MAPPING, serializedData] as const
			case StructureObjectDto.TypeEnum.MAPPING_READ:
				return [
					NamingDtoTypeEnum.MAPPING_CODE,
					SqlTypeEnum.MAPPING_READ,
					serializedData,
				] as const
			case StructureObjectDto.TypeEnum.MAPPING_WRITE:
				return [
					NamingDtoTypeEnum.MAPPING_CODE,
					SqlTypeEnum.MAPPING_WRITE,
					serializedData,
				] as const
			case StructureObjectDto.TypeEnum.WORKFLOW:
				return [
					NamingDtoTypeEnum.WORKFLOW_CODE,
					SqlTypeEnum.WORKFLOW,
					serializedData,
				] as const
			case StructureObjectDto.TypeEnum.API_NODE:
				return [
					NamingDtoTypeEnum.API_NODE_CODE,
					SqlTypeEnum.API_NODE,
					serializedData,
				] as const
			case StructureObjectDto.TypeEnum.API_COLLECTION:
				return [
					NamingDtoTypeEnum.API_COLLECTION_CODE,
					SqlTypeEnum.API_COLLECTION,
					serializedData,
				] as const
			case StructureObjectDto.TypeEnum.QUEUE:
				return [NamingDtoTypeEnum.QUEUE_CODE, SqlTypeEnum.QUEUE, serializedData] as const
			case StructureObjectDto.TypeEnum.DIAGRAM:
				return [NamingDtoTypeEnum.DIAGRAM_CODE, SqlTypeEnum.DIAGRAM, serializedData] as const
			default:
				return undefined
		}
	}

	const getGeneratedCode = async (name: string) => {
		if (!generateElementCode || !node?.id) {
			return ''
		}

		const namingConfig = getNamingConfig(name)
		if (namingConfig) {
			const [namingType, sqlType, serializedData] = namingConfig
			const { data: result } = await request(
				executeNamingOverWorkingData(namingType, node.id, sqlType, {
					body: { data: serializedData },
				}),
				{ disableError: true },
			)

			if (result?.result) {
				return formatCodeByCase(result.result, isUpperCase)
			}
		}

		const fallback = await adjustTableNameMutation({
			utilsNameRequestDto: { name },
		}).unwrap()

		return formatCodeByCase(fallback.result, isUpperCase)
	}

	const handleSubmit = async (form: Partial<FormData>) => {
		const name = form.name as string

		const codeValue = await getGeneratedCode(name)
		const nodeData = getNodeData(name, codeValue, form)

		const data = {
			type,
			data: JSON.stringify(nodeData),
		}

		const action = getAction(data, node, type, {
			diagramRef,
			isDiagramAddModal,
			node,
		})

		await dispatch(action)
		onRefresh?.()
		onClose()
	}

	return (
		<ModalForm<FormData>
			saveTitle={t('CREATE')}
			saveIcon={faPlus}
			cancelTitle={t('CANCEL')}
			onSubmit={handleSubmit}
			open={isOpen}
			onClose={onClose}
			contentStyle={{
				width: '400px',
			}}
			header={getHeader(type, t)}
		>
			<TextFormField
				title={t('NAME')}
				name="name"
				testId="createName"
				required
				validators={[uniqueNameValidator(dispatch, undefined, node?.id)]}
				autoFocus
				enableAutocomplete={false}
				refInput={inputRef}
			/>

			{isSystem && (
				<>
					<SelectFormField
						title={t('SYSTEM_TECHNOLOGY')}
						name="systemTechnologyId"
						testId="systemTechnology"
						options={systemTechnologies.data || []}
						fieldIsLoading={systemTechnologies.reloading}
						valueKey="id"
						labelKey="name"
						required
						isNumeric
					/>

					<SelectFormField
						title={t('WORKFLOW_TECHNOLOGY')}
						name="workflowTechnologyId"
						testId="workflowTechnology"
						options={workflowTechnologies.data || []}
						fieldIsLoading={workflowTechnologies.reloading}
						valueKey="id"
						labelKey="name"
						required
						isNumeric
					/>
				</>
			)}
			{isWriteMapping && (
				<AddModalWriteMapping onChangeWriteMapping={onChangeWriteMapping} />
			)}
		</ModalForm>
	)
}
